/**
 * Credit Memo List (SPRINT-25 F008)
 *
 * Card list of credit memos for a deal within the Deal Financial Hub.
 * Features:
 * - Empty state with "Create Credit Memo" button
 * - Card per memo: Title, Status badge, Prepared By, Version, Sections complete
 * - "Create New Memo" button (visible when memos exist)
 * - Click card → opens CreditMemoEditor
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Loader2, FileText, User } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { CreditMemoEditor } from "./credit-memo-editor";
import type { DealCreditMemo, DealCreditMemoSection } from "@shared/schema";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CreditMemoWithSections extends DealCreditMemo {
  sections?: DealCreditMemoSection[];
}

interface CreditMemoListProps {
  dealId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    draft: "Draft",
    in_review: "In Review",
    approved: "Approved",
    submitted: "Submitted",
  };
  return map[status] ?? status;
}

function statusVariant(
  status: string,
): "default" | "secondary" | "outline" | "destructive" {
  const map: Record<
    string,
    "default" | "secondary" | "outline" | "destructive"
  > = {
    draft: "secondary",
    in_review: "default",
    approved: "default",
    submitted: "outline",
  };
  return map[status] ?? "secondary";
}

function countCompleteSections(memo: CreditMemoWithSections): number {
  return (memo.sections ?? []).filter((s) => s.isComplete).length;
}

function totalSections(memo: CreditMemoWithSections): number {
  return (memo.sections ?? []).length;
}

function hasAutoGeneratedSections(memo: CreditMemoWithSections): boolean {
  return (memo.sections ?? []).some((s) => s.autoGenerated === true);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CreditMemoList({ dealId }: CreditMemoListProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedMemoId, setSelectedMemoId] = useState<string | null>(null);

  // -----------------------------------------------------------------------
  // Fetch memos list
  // -----------------------------------------------------------------------

  const { data: memos = [], isLoading } = useQuery<CreditMemoWithSections[]>({
    queryKey: [`/api/financial/deals/${dealId}/credit-memos`],
    enabled: !!dealId,
  });

  // -----------------------------------------------------------------------
  // Create memo mutation
  // -----------------------------------------------------------------------

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        "POST",
        `/api/financial/deals/${dealId}/credit-memos`,
        { title: `Credit Memo v${(memos.length + 1).toString()}` },
      );
      return (await response.json()) as CreditMemoWithSections;
    },
    onSuccess: (created: CreditMemoWithSections) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/financial/deals/${dealId}/credit-memos`],
      });
      toast({ title: "Credit memo created", duration: 2000 });
      // Open the newly created memo in the editor
      setSelectedMemoId(created.id);
    },
    onError: () => {
      toast({
        title: "Failed to create memo",
        description: "Could not create credit memo. Please try again.",
        variant: "destructive",
      });
    },
  });

  // -----------------------------------------------------------------------
  // Render: Editor view
  // -----------------------------------------------------------------------

  if (selectedMemoId) {
    return (
      <CreditMemoEditor
        dealId={dealId}
        memoId={selectedMemoId}
        onBack={() => setSelectedMemoId(null)}
      />
    );
  }

  // -----------------------------------------------------------------------
  // Render: List view
  // -----------------------------------------------------------------------

  return (
    <Card className="border-0 shadow-none" data-testid="panel-credit-memo-list">
      <CardHeader className="px-0 py-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-medium">Credit Memos</CardTitle>
        {memos.length > 0 && (
          <Button
            size="sm"
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
            data-testid="button-create-credit-memo"
          >
            {createMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-1.5" />
            )}
            Create New Memo
          </Button>
        )}
      </CardHeader>

      <CardContent className="px-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : memos.length === 0 ? (
          /* Empty state */
          <div
            className="flex flex-col items-center justify-center py-12 text-center"
            data-testid="credit-memo-empty-state"
          >
            <FileText className="h-10 w-10 text-muted-foreground mb-4" />
            <h3 className="text-sm font-medium mb-1">
              No credit memos created for this deal
            </h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-sm">
              Credit memos require a calculated consolidated analysis. Create
              one to document your qualitative assessment.
            </p>
            <Button
              size="sm"
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending}
              variant="outline"
              data-testid="button-create-credit-memo"
            >
              {createMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-1.5" />
              )}
              Create Credit Memo
            </Button>
          </div>
        ) : (
          /* Memo cards */
          <div className="space-y-3">
            {memos.map((memo) => {
              const complete = countCompleteSections(memo);
              const total = totalSections(memo);

              return (
                <Card
                  key={memo.id}
                  className="cursor-pointer hover:border-primary/50 transition-colors"
                  data-testid={`memo-card-${memo.id}`}
                  onClick={() => setSelectedMemoId(memo.id)}
                >
                  <CardContent className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <span className="font-medium text-sm truncate">
                            {memo.title}
                          </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                          {memo.preparedBy && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {memo.preparedBy}
                            </span>
                          )}
                          <span>v{memo.version ?? 1}</span>
                          {total > 0 && (
                            <span
                              className={
                                complete === total
                                  ? "text-green-600 font-medium"
                                  : ""
                              }
                            >
                              {complete}/{total} sections
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <Badge
                          variant={statusVariant(memo.status)}
                          data-testid={`badge-status-${memo.id}`}
                        >
                          {statusLabel(memo.status)}
                        </Badge>
                        {hasAutoGeneratedSections(memo) && (
                          <Badge
                            variant="outline"
                            data-testid={`badge-needs-review-${memo.id}`}
                            className="border-amber-400 text-amber-700 text-xs"
                          >
                            Needs review
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
